import React, { useState } from "react";
import {
  Card,
  Space as AntdSpace,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Input,
  DatePicker,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { ColumnsType } from "antd/es/table";
import {
  useReportsServiceGetCustomerReportV2,
  useReportsServiceDownloadCustomerReportV2,
} from "api/queries";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const CustomerOverview = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [periodFilter, setPeriodFilter] = useState("Custom");

  const offset = (page - 1) * limit;

  const company = useSelector((state: any) => state.company);

  const { data, isLoading } = useReportsServiceGetCustomerReportV2({
    limit,
    offset,
    search,
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
  });

  const { mutate: downloadCustomerReport, isPending: isDownloading } =
    useReportsServiceDownloadCustomerReportV2();

  const columns: ColumnsType<any> = [
    {
      title: "Name",
      dataIndex: "customerName",
      key: "customerName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (country: string) => country || "-",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (address: string) => address || "-",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Total Invoices",
      dataIndex: "totalInvoices",
      key: "totalInvoices",
    },
    {
      title: "Total Revenue",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      render: (revenue: number) =>
        revenue
          ? `${company.currencySymbol || "$"}${revenue.toLocaleString()}`
          : "-",
    },
  ];

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        // Custom
        break;
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
  };

  const handleTableChange = (pagination: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 10);
    }
  };

  const handleDownload = () => {
    downloadCustomerReport(
      {
        search,
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, "_blank");
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <AntdSpace direction="vertical" size="large" style={{ width: "100%" }}>
        <AntdSpace style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Customer Overview Report</Title>
          <AntdSpace>
            <Input.Search
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name, email, phone, etc."
              style={{ width: 200 }}
            />
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloading}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </AntdSpace>
        </AntdSpace>
        {/* Summary Statistics */}
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Customers"
                value={data?.kpis?.totalCustomers || 0}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Invoices"
                value={data?.kpis?.totalInvoices || 0}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={data?.kpis?.totalRevenue || 0}
                prefix={company.currencySymbol || "$"}
                loading={isLoading}
              />
            </Card>
          </Col>
        </Row>
        {/* Main Data Table */}
        <Table
          columns={columns}
          dataSource={data?.items || []}
          pagination={{
            current: page,
            pageSize: limit,
            total: data?.total || 0,
            onChange: (page, pageSize) => {
              setPage(page);
              setLimit(pageSize);
            },
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} customers`,
          }}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          rowKey={(item) => item.customerId}
          onChange={handleTableChange}
        />
      </AntdSpace>
    </motion.div>
  );
};

export default CustomerOverview;
